// src/components/products/forms/EditProductForm.tsx
import React, { useState } from "react";
import { FiPlus, FiTrash2, FiPercent } from "react-icons/fi";
import { FaIndianRupeeSign } from "react-icons/fa6";

import type {
  ProductWithConfig,
  UpdateProductPayload,
  ProductTaxRulePayload,
  ProductEmailPayload,
  TaxMethod,
} from "@/api/features/products/types";

import {
  MarkupsEditor,
  markupRowsFromConfig,
  markupRowsToPayload,
  markupRowError,
  type MarkupRow,
} from "./MarkupsEditor";

type Props = {
  value: ProductWithConfig;
  onSubmit: (payload: UpdateProductPayload) => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
  apiError?: string | null;
};

type TaxRuleRow = {
  id?: number;
  taxName: string;
  taxRate: string; // keep as string in UI, convert on submit
  taxMethod: TaxMethod;
};

type EmailRow = {
  id?: number;
  email: string;
  label: string;
};

export const EditProductForm: React.FC<Props> = ({
  value,
  onSubmit,
  onCancel,
  isSubmitting = false,
  apiError,
}) => {
  /* ------------------------------------------------------------
   * Top-level fields
   * ------------------------------------------------------------ */

  const [name, setName] = useState(value.name ?? "");

  // Markups (scoped: customerType x travelScope) hydrated from config
  const [markups, setMarkups] = useState<MarkupRow[]>(
    markupRowsFromConfig(value.markups)
  );

  /* ------------------------------------------------------------
   * Tax rules
   * ------------------------------------------------------------ */

  const [taxRules, setTaxRules] = useState<TaxRuleRow[]>(
    (value.taxRules ?? []).map((t) => ({
      id: t.id,
      taxName: t.taxName ?? "",
      taxRate:
        t.taxRate === null || t.taxRate === undefined
          ? ""
          : String(t.taxRate),
      taxMethod: t.taxMethod ?? "PERCENT",
    }))
  );

  const handleAddTaxRule = () => {
    setTaxRules((prev) => [
      ...prev,
      {
        taxName: "",
        taxRate: "",
        taxMethod: "PERCENT",
      },
    ]);
  };

  const handleRemoveTaxRule = (index: number) => {
    // Removing from array means it won't be sent -> BE will soft-delete it
    setTaxRules((prev) => prev.filter((_, i) => i !== index));
  };

  const updateTaxRule = <K extends keyof TaxRuleRow>(
    index: number,
    key: K,
    value: TaxRuleRow[K]
  ) => {
    setTaxRules((prev) =>
      prev.map((row, i) =>
        i === index ? { ...row, [key]: value } : row
      )
    );
  };

  /* ------------------------------------------------------------
   * Emails
   * ------------------------------------------------------------ */

  const [emails, setEmails] = useState<EmailRow[]>(
    (value.emails ?? []).map((e) => ({
      id: e.id,
      email: e.email ?? "",
      label: e.label ?? "",
    }))
  );

  const handleAddEmail = () => {
    setEmails((prev) => [
      ...prev,
      {
        email: "",
        label: "",
      },
    ]);
  };

  const handleRemoveEmail = (index: number) => {
    // Removing from array means it won't be sent -> BE will soft-delete it
    setEmails((prev) => prev.filter((_, i) => i !== index));
  };

  const updateEmail = <K extends keyof EmailRow>(
    index: number,
    key: K,
    value: EmailRow[K]
  ) => {
    setEmails((prev) =>
      prev.map((row, i) =>
        i === index ? { ...row, [key]: value } : row
      )
    );
  };

  /* ------------------------------------------------------------
   * Submit
   * ------------------------------------------------------------ */

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedName = name.trim();

    const payloadTaxRules: ProductTaxRulePayload[] = taxRules
      .filter((t) => t.taxName.trim() !== "")
      .map((t) => {
        const base: ProductTaxRulePayload = {
          taxName: t.taxName.trim(),
          taxRate:
            t.taxRate === "" ? 0 : Number(t.taxRate) || 0,
          taxMethod: t.taxMethod,
        };
        if (typeof t.id === "number") {
          base.id = t.id;
        }
        return base;
      });

    const payloadEmails: ProductEmailPayload[] = emails
      .filter((e) => e.email.trim() !== "")
      .map((e) => {
        const base: ProductEmailPayload = {
          email: e.email.trim(),
          label:
            e.label.trim() === ""
              ? null
              : e.label.trim(),
        };
        if (typeof e.id === "number") {
          base.id = e.id;
        }
        return base;
      });

    // Invalid markup rates are shown inline; never submit them.
    if (markups.some((row) => markupRowError(row))) return;

    const payload: UpdateProductPayload = {
      name: trimmedName || undefined,

      // Scoped markups (authoritative): backend diffs create/update/delete.
      markups: markupRowsToPayload(markups),

      taxRules: payloadTaxRules,
      emails: payloadEmails,
    };

    onSubmit(payload);
  };

  /* ------------------------------------------------------------
   * Render
   * ------------------------------------------------------------ */

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Top level error */}
      {apiError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {apiError}
        </div>
      )}

      {/* Basic info */}
      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Field label="Product name *">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Flights"
              required
              disabled={isSubmitting}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none placeholder:text-gray-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 disabled:bg-gray-100"
            />
          </Field>
        </div>

        {/* Slug as read-only info */}
        <div>
          <Field label="Slug (auto-generated)">
            <div className="flex items-center rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
              <span className="truncate">
                {value.slug || "Will be generated from name"}
              </span>
            </div>
          </Field>
          <p className="mt-1 text-[11px] text-gray-500">
            Slug is generated in backend from the name. It updates
            automatically when you change the product name.
          </p>
        </div>
      </section>

      {/* Markups */}
      <MarkupsEditor
        value={markups}
        onChange={setMarkups}
        disabled={isSubmitting}
        productName={name}
      />

      {/* Tax rules */}
      <section className="space-y-3 rounded-xl border border-gray-200 bg-gray-50 p-4">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-800">
            Tax rules
          </h3>
          <button
            type="button"
            onClick={handleAddTaxRule}
            disabled={isSubmitting}
            className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            <FiPlus className="h-3 w-3" />
            Add tax rule
          </button>
        </div>
        <p className="text-xs text-gray-500">
          Adjust GST / service-fee style taxes for this product.
          Removing a rule will exclude it from the update payload
          (backend will soft delete).
        </p>

        <div className="space-y-3">
          {taxRules.length === 0 && (
            <div className="rounded-lg border border-dashed border-gray-200 bg-white px-3 py-2 text-xs text-gray-500">
              No tax rules configured. Add one if needed.
            </div>
          )}

          {taxRules.map((row, index) => (
            <div
              key={row.id ?? `tax-new-${index}`}
              className="rounded-lg border border-gray-200 bg-white p-3"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 space-y-2">
                  <Field label="Tax name *">
                    <input
                      type="text"
                      value={row.taxName}
                      onChange={(e) =>
                        updateTaxRule(
                          index,
                          "taxName",
                          e.target.value
                        )
                      }
                      placeholder="e.g., GST, Service Fee"
                      disabled={isSubmitting}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none placeholder:text-gray-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 disabled:bg-gray-100"
                    />
                  </Field>

                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <Field label="Tax rate *">
                      <div className="relative">
                        {row.taxMethod === "FLAT" && (
                          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                            <FaIndianRupeeSign className="h-4 w-4" />
                          </span>
                        )}
                        <input
                          type="number"
                          min={0}
                          step={0.01}
                          value={row.taxRate}
                          onChange={(e) =>
                            updateTaxRule(
                              index,
                              "taxRate",
                              e.target.value
                            )
                          }
                          placeholder="0"
                          disabled={isSubmitting}
                          className={`w-full rounded-lg border border-gray-300 bg-white ${
                            row.taxMethod === "FLAT"
                              ? "pl-9"
                              : "pl-3"
                          } ${
                            row.taxMethod === "PERCENT"
                              ? "pr-9"
                              : "pr-3"
                          } py-2 text-sm outline-none placeholder:text-gray-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 disabled:bg-gray-100`}
                        />
                        {row.taxMethod === "PERCENT" && (
                          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                            <FiPercent className="h-4 w-4" />
                          </span>
                        )}
                      </div>
                    </Field>

                    <Field label="Method">
                      <div className="relative">
                        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                          {row.taxMethod === "PERCENT" ? (
                            <FiPercent className="h-4 w-4" />
                          ) : (
                            <FaIndianRupeeSign className="h-4 w-4" />
                          )}
                        </span>
                        <select
                          value={row.taxMethod}
                          onChange={(e) =>
                            updateTaxRule(
                              index,
                              "taxMethod",
                              e.target.value as TaxMethod
                            )
                          }
                          disabled={isSubmitting}
                          className="w-full appearance-none rounded-lg border border-gray-300 bg-white pl-9 pr-8 py-2 text-sm outline-none text-gray-900 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 disabled:bg-gray-100"
                        >
                          <option value="PERCENT">Percent</option>
                          <option value="FLAT">Flat</option>
                        </select>
                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                          ▾
                        </span>
                      </div>
                    </Field>
                  </div>
                </div>

                {taxRules.length > 0 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveTaxRule(index)}
                    disabled={isSubmitting}
                    className="mt-1 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-red-500 hover:bg-red-50 disabled:opacity-60"
                    aria-label="Remove tax rule"
                  >
                    <FiTrash2 />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Emails */}
      <section className="space-y-3 rounded-xl border border-gray-200 bg-gray-50 p-4">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-800">
            Product emails
          </h3>
          <button
            type="button"
            onClick={handleAddEmail}
            disabled={isSubmitting}
            className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            <FiPlus className="h-3 w-3" />
            Add email
          </button>
        </div>
        <p className="text-xs text-gray-500">
          Update ops/support email IDs for this product. Removing
          an email will exclude it from the update payload (backend
          will soft delete).
        </p>

        <div className="space-y-3">
          {emails.length === 0 && (
            <div className="rounded-lg border border-dashed border-gray-200 bg-white px-3 py-2 text-xs text-gray-500">
              No emails configured. Add at least one if needed.
            </div>
          )}

          {emails.map((row, index) => (
            <div
              key={row.id ?? `email-new-${index}`}
              className="rounded-lg border border-gray-200 bg-white p-3"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 space-y-2">
                  <Field
                    label={`Email ${index + 1}${
                      index === 0 ? " *" : ""
                    }`}
                  >
                    <input
                      type="email"
                      value={row.email}
                      onChange={(e) =>
                        updateEmail(index, "email", e.target.value)
                      }
                      placeholder="name@tranzy.com"
                      required={index === 0}
                      disabled={isSubmitting}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none placeholder:text-gray-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 disabled:bg-gray-100"
                    />
                  </Field>

                  <Field label="Label (optional)">
                    <input
                      type="text"
                      value={row.label}
                      onChange={(e) =>
                        updateEmail(index, "label", e.target.value)
                      }
                      placeholder="e.g., Support, Operations"
                      disabled={isSubmitting}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none placeholder:text-gray-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 disabled:bg-gray-100"
                    />
                  </Field>
                </div>

                {emails.length > 0 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveEmail(index)}
                    disabled={isSubmitting}
                    className="mt-1 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-red-500 hover:bg-red-50 disabled:opacity-60"
                    aria-label="Remove email"
                  >
                    <FiTrash2 />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Footer buttons */}
      <div className="mt-4 flex items-center justify-end gap-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            disabled={isSubmitting}
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={isSubmitting}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-60"
        >
          {isSubmitting && (
            <span className="h-4 w-4 animate-spin rounded-full border border-white/50 border-t-transparent" />
          )}
          Save changes
        </button>
      </div>
    </form>
  );
};

/* ======================================================================
 * Small UI helpers
 * ====================================================================== */

const Field: React.FC<{
  label: string;
  children: React.ReactNode;
}> = ({ label, children }) => (
  <label className="block">
    <div className="mb-1 text-xs font-medium text-gray-700">
      {label}
    </div>
    {children}
  </label>
);

